import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const COST_PER_SECOND = 0.12 * 1_000_000 * (1 + BRAND_MARKUP);
const COST_PER_SECOND_HIGH = 0.25 * 1_000_000 * (1 + BRAND_MARKUP);

const REPLICATE_COST_PER_SECOND_HIGH = 0.17 * 1_000_000 * (1 + BRAND_MARKUP);
const REPLICATE_COST_PER_SECOND_BASIC = 0.13 * 1_000_000 * (1 + BRAND_MARKUP);

const RUNWARE_COST_PER_SECOND_HIGH = 0.16 * 1_000_000 * (1 + BRAND_MARKUP);
const RUNWARE_COST_PER_SECOND_BASIC = 0.07 * 1_000_000 * (1 + BRAND_MARKUP);

// Runware enforces a fixed set of width/height pairs per model (returned as
// `allowedValues` on `unsupportedModelResolution` errors). Picking nearest
// match per aspect ratio at each quality tier.
const RUNWARE_DIMENSIONS = {
  high: {
    "16:9": { width: 1280, height: 720 },
    "9:16": { width: 720, height: 1280 },
    "4:3": { width: 1112, height: 834 },
    "3:4": { width: 834, height: 1112 },
  },
  basic: {
    "16:9": { width: 864, height: 496 },
    "9:16": { width: 496, height: 864 },
    "4:3": { width: 752, height: 560 },
    "3:4": { width: 560, height: 752 },
  },
} as const;

const seedance2Params = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16", "4:3", "3:4"]).default("9:16"),
    duration: z.enum(["5", "10", "15"]).default("5"),
    quality: z.enum(["basic", "high"]).default("high"),
    generateAudio: z.boolean().default(true),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const seedance2 = defineModel({
  key: "seedance-2",
  stepType: "video",
  spec: {
    label: "Seedance 2 (Default)",
    description:
      "High-quality video generation with optional image input for image-to-video mode",
    icon: "seedance-icon.png",
    tags: ["video-gen"],
    featured: true,
    info: "Generation takes 5-10 minutes. Optionally connect a Start Image for image-to-video mode.",
    features: {
      aspectRatio: true,
      duration: true,
      quality: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Start Image",
          slot: "input_image",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "16:9", value: "16:9" },
          { label: "9:16", value: "9:16" },
          { label: "4:3", value: "4:3" },
          { label: "3:4", value: "3:4" },
        ],
      },
      duration: {
        items: [
          { label: "5s", value: "5" },
          { label: "10s", value: "10" },
          { label: "15s", value: "15" },
        ],
      },
      quality: {
        items: [
          { label: "Basic", value: "basic" },
          { label: "High", value: "high" },
        ],
      },
    },
    params: seedance2Params,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/bytedance/seedance-2.0/text-to-video",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 303_400 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "bytedance:seedance@2.0",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const perSecond =
          params.quality === "high"
            ? RUNWARE_COST_PER_SECOND_HIGH
            : RUNWARE_COST_PER_SECOND_BASIC;
        return Math.ceil(perSecond * duration);
      },
      estimateCostPerSecond(params) {
        return params.quality === "high"
          ? RUNWARE_COST_PER_SECOND_HIGH
          : RUNWARE_COST_PER_SECOND_BASIC;
      },
      costPerSecond: RUNWARE_COST_PER_SECOND_HIGH,
      toPayload(params) {
        const imageUrl = params.attachments?.find(
          (att) => att.slot === "input_image",
        )?.data;
        const hasPrompt = !!params.prompt && params.prompt.trim().length >= 2;
        // Text-to-video with no prompt produces garbage; require one of the two.
        if (!imageUrl && !hasPrompt) {
          throw new ValidationError("Add a prompt or attach a start image.");
        }

        const size = RUNWARE_DIMENSIONS[params.quality][params.aspectRatio];

        return {
          model: this.modelId,
          positivePrompt: hasPrompt ? params.prompt : "--",
          duration: Number(params.duration),
          ...(imageUrl
            ? { inputs: { frameImages: [imageUrl] } }
            : { width: size.width, height: size.height }),
          settings: {
            audio: params.generateAudio ?? true,
          },
        };
      },
    },
    {
      provider: "replicate",
      modelId: "bytedance/seedance-2.0",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const perSecond =
          params.quality === "high"
            ? REPLICATE_COST_PER_SECOND_HIGH
            : REPLICATE_COST_PER_SECOND_BASIC;
        return Math.ceil(perSecond * duration);
      },
      estimateCostPerSecond(params) {
        return params.quality === "high"
          ? REPLICATE_COST_PER_SECOND_HIGH
          : REPLICATE_COST_PER_SECOND_BASIC;
      },
      costPerSecond: REPLICATE_COST_PER_SECOND_HIGH,
      toPayload(params) {
        const imageUrl = params.attachments?.find(
          (att) => att.slot === "input_image",
        )?.data;

        const model =
          params.quality === "high"
            ? ("bytedance/seedance-2.0" as const)
            : ("bytedance/seedance-2.0-fast" as const);

        return {
          model,
          input: {
            prompt: params.prompt || undefined,
            ...(imageUrl ? { image: imageUrl } : {}),
            resolution: "720p" as const,
            duration: Number(params.duration),
            aspect_ratio: params.aspectRatio,
            generate_audio: params.generateAudio ?? true,
          },
        };
      },
    },
    {
      provider: "muapi",
      modelId: "seedance-2-image-to-video",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const costPerSecond =
          params.quality === "high" ? COST_PER_SECOND_HIGH : COST_PER_SECOND;

        return Math.ceil(costPerSecond * duration);
      },
      estimateCostPerSecond(params) {
        return params.quality === "high"
          ? COST_PER_SECOND_HIGH
          : COST_PER_SECOND;
      },
      costPerSecond: COST_PER_SECOND,
      toPayload(params) {
        const imageUrl = params.attachments?.find(
          (att) => att.slot === "input_image",
        )?.data;

        // Route to I2V or T2V based on whether an image is provided
        const isI2v = !!imageUrl;
        const isFast = params.quality === "basic";

        const endpoint = isI2v
          ? isFast
            ? "seedance-2-image-to-video-fast"
            : "seedance-2-image-to-video"
          : isFast
            ? "seedance-2-text-to-video-fast"
            : "seedance-2-text-to-video";

        return {
          endpoint,
          prompt: params.prompt || undefined,
          ...(isI2v ? { images_list: [imageUrl] } : {}),
          aspect_ratio: params.aspectRatio,
          duration: Number(params.duration),
          generate_audio: params.generateAudio ?? true,
          remove_watermark: true,
        };
      },
    },
  ],
});
